import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, CreditCard, Loader2, Sparkles } from 'lucide-react';
import { panelPath } from '../../config/paths';
import { useAuth } from '../../context/AuthContext';
import { createCheckout, resumeSubscription } from '../../api/client';
import { AppError } from '../../api/request';
import { useIsPro } from '../../hooks/useIsPro';

const PRO_MONTHLY_PRICE = 20;
/** Yearly = pay 10 months, get 2 free. */
const PRO_YEARLY_TOTAL = PRO_MONTHLY_PRICE * 10;
const PRO_YEARLY_EFFECTIVE_MONTHLY = Math.round((PRO_YEARLY_TOTAL / 12) * 100) / 100;

export default function PlanPurchasePage() {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const { isPro } = useIsPro();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan') ?? 'pro';
  const billingFromUrl = searchParams.get('billing') === 'yearly' ? 'yearly' : 'monthly';
  const [billing, setBilling] = useState<'monthly' | 'yearly'>(billingFromUrl);
  const [loading, setLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const price = billing === 'monthly' ? PRO_MONTHLY_PRICE : PRO_YEARLY_EFFECTIVE_MONTHLY;
  const isProPlan = planFromUrl === 'pro';
  const showReactivate = user?.canReactivateSubscription === true;
  const hasActiveMembership = isPro && !showReactivate;
  const canClaimOffer = !isPro;

  const handleResume = async () => {
    setResumeError(null);
    setResumeLoading(true);
    try {
      const updated = await resumeSubscription();
      updateUser(updated);
    } catch (err) {
      setResumeError(err instanceof AppError ? err.userMessage : t('profile.reactivateError'));
    } finally {
      setResumeLoading(false);
    }
  };

  const handleProCheckout = async () => {
    if (!canClaimOffer) return;
    setError(null);
    setLoading(true);
    try {
      const { url } = await createCheckout(billing);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof AppError ? err.userMessage : t('planPurchase.checkoutError'));
    } finally {
      setLoading(false);
    }
  };

  if (hasActiveMembership) {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <div className="rounded-2xl border-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20 p-6 sm:p-8 shadow-lg shadow-primary-500/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('planPurchase.alreadyActiveTitle')}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {t('planPurchase.alreadyActiveSubtitle')}
              </p>
            </div>
          </div>
          <Link
            to={panelPath('profile')}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl font-medium bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
          >
            {t('planPurchase.viewSubscription')}
          </Link>
          <p className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate(panelPath(''))}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              {t('planPurchase.backToPanel')}
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      {showReactivate && (
        <div className="mb-6 rounded-2xl border-2 border-amber-500 bg-amber-50/50 dark:bg-amber-900/20 p-6 shadow-lg">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {t('planPurchase.reactivateTitle')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 mb-4">
            {t('planPurchase.reactivateSubtitle')}
          </p>
          {resumeError && (
            <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {resumeError}
            </div>
          )}
          <button
            type="button"
            onClick={handleResume}
            disabled={resumeLoading}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-70"
          >
            {resumeLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
            {resumeLoading ? t('common.loading') : t('profile.reactivateSubscription')}
          </button>
        </div>
      )}

      {canClaimOffer && (
      <>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
          <Sparkles className="h-6 w-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('planPurchase.completeTitle')}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            {t('planPurchase.completeSubtitle')}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20 p-6 sm:p-8 shadow-lg shadow-primary-500/10">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          {isProPlan ? t('home.proName') : t('home.enterpriseName')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {isProPlan ? t('home.proDesc') : t('home.enterpriseDesc')}
        </p>

        {isProPlan && (
        <div className="mt-6 flex items-center gap-2">
          <span
            className={`text-sm font-medium cursor-pointer ${
              billing === 'monthly' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
            }`}
            onClick={() => setBilling('monthly')}
          >
            {t('home.monthly')}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={billing === 'yearly'}
            onClick={() => setBilling((b) => (b === 'monthly' ? 'yearly' : 'monthly'))}
            className="relative shrink-0 w-11 h-6 rounded-full bg-gray-300 dark:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                billing === 'yearly' ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <span
            className={`text-sm font-medium cursor-pointer ${
              billing === 'yearly' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
            }`}
            onClick={() => setBilling('yearly')}
          >
            {t('home.yearly')}
          </span>
        </div>
        )}

        {isProPlan && (
        <div className="mt-4 min-h-[3rem]">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">${price}</span>
            <span className="text-gray-500 dark:text-gray-400">{t('home.perMonth')}</span>
          </div>
          <p className={`mt-1 text-xs text-gray-600 dark:text-gray-400 ${billing === 'yearly' ? '' : 'invisible'}`}>
            {t('home.proYearlyBilledHint', { price: PRO_YEARLY_TOTAL })}
          </p>
        </div>
        )}

        <ul className="mt-6 space-y-3">
          {(isProPlan ? ['home.proFeature1', 'home.proFeature2', 'home.proFeature3', 'home.proFeature4', 'home.proFeature5'] : ['home.enterpriseFeature1', 'home.enterpriseFeature2', 'home.enterpriseFeature3']).map(
            (k) => (
              <li key={k} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Check className="h-5 w-5 text-primary-600 dark:text-primary-400 shrink-0" />
                {t(k)}
              </li>
            )
          )}
        </ul>

        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          {isProPlan ? (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('planPurchase.paymentNotePro')}
              </p>
              {error && (
                <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}
              <button
                type="button"
                onClick={handleProCheckout}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl font-medium bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors disabled:opacity-70"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <CreditCard className="h-5 w-5" />
                )}
                {loading ? t('planPurchase.redirecting') : t('planPurchase.ctaPro')}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('planPurchase.paymentNote')}
              </p>
              <a
                href={`mailto:sales@buildmate.app?subject=${encodeURIComponent(t('planPurchase.enterprisePlanRequestSubject'))}`}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl font-medium bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
              >
                <CreditCard className="h-5 w-5" />
                {t('planPurchase.cta')}
              </a>
            </>
          )}
          <p className="mt-4 text-center">
            <Link
              to={panelPath('')}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              {t('planPurchase.skipForNow')}
            </Link>
          </p>
        </div>
      </div>
      </>
      )}

      {showReactivate && !canClaimOffer && (
        <p className="mt-4 text-center">
          <Link
            to={panelPath('')}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
          >
            {t('planPurchase.backToPanel')}
          </Link>
        </p>
      )}
    </div>
  );
}
